import clsx from 'clsx';
import { isEmpty } from 'lodash-es';
import {
  forwardRef,
  type JSX,
  type PropsWithChildren,
  useImperativeHandle,
  useRef,
} from 'react';

import { TreeMapChartLayout } from './components/TreeMapChartLayout.js';
import { DEFAULT_HEIGHT, DEFAULT_WIDTH } from './constants.js';
import { useTreeMapChartConfig } from './hooks/useTreeMapChartConfig.js';
import { TreeMapChartProviders } from './providers/TreeMapChartProviders.js';
import { Tooltip } from './slots/Tooltip.js';
import { Legend as TreeMapChartLegend } from './slots/TreeMapChartLegend.js';
import type { TreeMapElementRef, TreeMapProps } from './types/treemap.js';
import { buildAndDownloadCsv } from './utils/build-and-download-csv.js';
import { getSortedTree } from './utils/get-sorted-tree.js';
import { mapToInternalTree } from './utils/map-to-internal-tree.js';
import { Intent } from '../../core/slots/Intent/Intent.js';
import { defaultChartSize } from '../../core/styles/defaultChartValues.sty.js';
import { getChartStatesTemplates } from '../core/components/chart-states/utils/get-chart-states-template.js';
import { EmptyStateSlot } from '../core/slots/chart-states/EmptyStateSlot.js';
import { ErrorStateSlot } from '../core/slots/chart-states/ErrorStateSlot.js';
import { ChartType } from '../core/types/chart-types.js';

const TreeMapRoot = forwardRef<
  TreeMapElementRef,
  PropsWithChildren<TreeMapProps>
>((props, forwardedRef): JSX.Element => {
  const {
    data,
    children,
    seriesActions,
    width = DEFAULT_WIDTH,
    height = DEFAULT_HEIGHT,
    className,
    style,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    'data-testid': dataTestId = 'treemap-chart-root',
  } = props;

  const config = useTreeMapChartConfig(props);
  const { loading } = config;

  const emptyData =
    isEmpty(data) ||
    data.tree.nodes === undefined ||
    data.tree.nodes.length === 0;

  const { emptyState, errorState } = getChartStatesTemplates(children);

  const treeMapRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(forwardedRef, () => {
    let exposedRef = {} as TreeMapElementRef;

    if (treeMapRef.current) {
      exposedRef = {
        element: treeMapRef.current,
        getConfig: () => JSON.stringify({ type: ChartType.Treemap, ...config }),
        downloadData: () => buildAndDownloadCsv(data.tree),
      };
    }

    return exposedRef;
  });

  const internalTree = mapToInternalTree(
    data.tree,
    config.formatter,
    data.unit,
  );
  const sortedTree = getSortedTree(internalTree);

  return (
    <TreeMapChartProviders
      data={data}
      seriesActions={seriesActions}
      {...config}
    >
      <TreeMapChartLayout
        ref={treeMapRef}
        data={sortedTree}
        width={width}
        height={height}
        emptyState={emptyState}
        errorState={errorState}
        isEmpty={emptyData}
        loading={loading}
        className={clsx(defaultChartSize, className)}
        style={style}
        data-dtrum-mask={dataDtrumMask}
        data-dtrum-allow={dataDtrumAllow}
        data-testid={dataTestId}
      />
    </TreeMapChartProviders>
  );
});

/**
 * The `TreeMap` visualizes the distribution of hierarchical data.
 * It uses nested rectangles of different sizes and colors to show
 * how much each category or subcategory contributes to the whole
 * data set.
 * @public
 */
export const TreeMap = Object.assign(TreeMapRoot, {
  Legend: TreeMapChartLegend,
  Tooltip,
  Intent,
  EmptyState: EmptyStateSlot,
  ErrorState: ErrorStateSlot,
});
